import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, withStyles } from '@material-ui/styles';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Typography,
  Box,
  Divider
} from '@material-ui/core';
import { getInitials } from '../../helpers';
import { getTimeFromXDisplay } from '../../helpers';

// Styled badge for online status indicator
const OnlineBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: '$ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""'
    }
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0
    }
  }
}))(Badge);

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    backgroundColor: theme.palette.background.paper
  },
  listItem: {
    padding: theme.spacing(2),
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  activeListItem: {
    backgroundColor: theme.palette.action.selected,
    borderLeft: `4px solid ${theme.palette.primary.main}`
  },
  avatar: {
    width: 50,
    height: 50,
    backgroundColor: theme.palette.primary.main
  },
  unreadBadge: {
    // Only target the direct badge, not nested ones
    '& > .MuiBadge-badge': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    }
  },
  lastMessage: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary
  },
  timestamp: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(4),
    textAlign: 'center'
  }
}));

const ConversationList = ({
  conversations,
  currentConversation,
  onConversationClick,
  onlineUsers,
  currentUserId
}) => {
  const classes = useStyles();

  // Helper to determine the other user's ID in a conversation
  const getOtherUserId = (conversation) => {
    if (conversation.companyUserId === currentUserId) {
      return conversation.candidateUserId;
    }
    return conversation.companyUserId;
  };

  if (!conversations || conversations.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Typography variant="h6" color="textSecondary">
          No conversations yet
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Start a conversation to begin messaging
        </Typography>
      </div>
    );
  }

  return (
    <List className={classes.root}>
      {conversations.map((conversation, index) => {
        const isActive =
          currentConversation && currentConversation.id === conversation.id;
        const unreadCount = conversation.unreadCount || 0;
        const lastMessage = conversation.lastMessage;
        const otherUserName =
          conversation.otherUserName || conversation.companyName || 'Unknown User';
        const otherUserImageUrl = 
          conversation.otherUserImageUrl || conversation.companyLogoUrl;
        const otherUserId = getOtherUserId(conversation);
        const isOnline = onlineUsers && onlineUsers[otherUserId];

        return (
          <React.Fragment key={conversation.id}>
            <ListItem
              className={`${classes.listItem} ${
                isActive ? classes.activeListItem : ''
              }`}
              onClick={() => onConversationClick(conversation)}>
              <ListItemAvatar>
                <Badge
                  badgeContent={unreadCount}
                  className={classes.unreadBadge}
                  overlap="rectangular"
                  invisible={unreadCount === 0}>
                  <OnlineBadge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    invisible={!isOnline}>
                    <Avatar 
                      className={classes.avatar}
                      src={otherUserImageUrl || undefined}
                    >
                      {!otherUserImageUrl && getInitials(otherUserName)}
                    </Avatar>
                  </OnlineBadge>
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center">
                    <Typography variant="subtitle1" component="span">
                      {otherUserName}
                    </Typography>
                    {lastMessage && (
                      <Typography
                        variant="caption"
                        className={classes.timestamp}>
                        {getTimeFromXDisplay(lastMessage.createdAt)}
                      </Typography>
                    )}
                  </Box>
                }
                secondary={
                  lastMessage && (
                    <Typography
                      variant="body2"
                      className={classes.lastMessage}>
                      {lastMessage.content}
                    </Typography>
                  )
                }
              />
            </ListItem>
            {index < conversations.length - 1 && <Divider />}
          </React.Fragment>
        );
      })}
    </List>
  );
};

ConversationList.propTypes = {
  conversations: PropTypes.array.isRequired,
  currentConversation: PropTypes.object,
  onConversationClick: PropTypes.func.isRequired,
  onlineUsers: PropTypes.object,
  currentUserId: PropTypes.string
};

export default ConversationList;

